import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, App as AntApp, Button, Card, Collapse, Empty, Flex, Modal, Result, Skeleton, Tabs, Tag, Typography,
} from 'antd';
import {
  BookOutlined, CheckCircleOutlined, CloudDownloadOutlined, FileTextOutlined, FolderOpenOutlined,
  ReloadOutlined, RocketOutlined, TeamOutlined,
} from '@ant-design/icons';
import { baixarKit, listarMateriais, mensagemErro, obterAcesso, obterInicio, obterMaterial } from './api';
import MarkdownSeguro from './MarkdownSeguro';
import { PainelAcervo, PainelEquipe } from './Paineis';
import { InicioNexo, Material, MaterialResumo } from './types';
import './Nexo.css';

const { Title, Paragraph, Text } = Typography;
type Aba = 'inicio' | 'materiais' | 'equipe' | 'acervo';
const abasValidas: Aba[] = ['inicio', 'materiais', 'equipe', 'acervo'];
const pedidoInicial = `Prepare esta pasta para trabalhar no padrão Hydrodata NEXO.
Leia AGENTS.md e CLAUDE.md. Confira o que já está pronto e faça a preparação técnica.
Peça só os logins ou decisões que dependem de mim.
Ao terminar, diga o que posso testar e o que ainda falta.
Meu primeiro problema é: [descreva em uma frase].`;

function abaDaUrl(): Aba {
  const valor = new URLSearchParams(window.location.search).get('aba') as Aba | null;
  return valor && abasValidas.includes(valor) ? valor : 'inicio';
}

function revisaoCurta(revisao: string | number) {
  const valor = String(revisao);
  return /^[0-9a-f]{40}$/i.test(valor) ? valor.slice(0, 7) : valor;
}

function fonteMaterial(origem: string) {
  const rotulo = origem.replace(/\\/g, '/').split('/').pop() || 'arquivo de origem';
  const href = /^https?:\/\//i.test(origem)
    ? origem
    : `https://github.com/hydrodataeng/nexo/blob/main/${encodeURI(origem.replace(/^\/+/, ''))}`;
  return { rotulo, href };
}

function NexoConteudo() {
  const { message } = AntApp.useApp();
  const [aba, setAba] = useState<Aba>(abaDaUrl);
  const [dados, setDados] = useState<InicioNexo | null>(null);
  const [materiais, setMateriais] = useState<MaterialResumo[]>([]);
  const [permitido, setPermitido] = useState<boolean | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [materialAberto, setMaterialAberto] = useState<Material | null>(null);
  const [carregandoMaterial, setCarregandoMaterial] = useState(false);
  const [baixando, setBaixando] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true); setErro('');
    try {
      const acesso = await obterAcesso();
      const podeConsultar = acesso.permitido && acesso.permissoes.consultar;
      setPermitido(podeConsultar);
      if (!podeConsultar) { setDados(null); setMateriais([]); return; }
      const [inicio, biblioteca] = await Promise.all([obterInicio(), listarMateriais()]);
      if (!inicio.permissoes.consultar) throw new Error('Seu acesso de consulta ao NEXO não está ativo.');
      setDados(inicio);
      setMateriais(biblioteca);
    } catch (e) {
      setPermitido(null); setDados(null); setMateriais([]); setErro(mensagemErro(e));
    } finally { setCarregando(false); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    const aoVoltar = () => setAba(abaDaUrl());
    window.addEventListener('popstate', aoVoltar);
    return () => window.removeEventListener('popstate', aoVoltar);
  }, []);

  const trocarAba = (chave: string) => {
    const novaAba = chave as Aba;
    setAba(novaAba);
    const url = new URL(window.location.href);
    if (novaAba === 'inicio') url.searchParams.delete('aba'); else url.searchParams.set('aba', novaAba);
    window.history.pushState({}, '', `${url.pathname}${url.search}${url.hash}`);
  };

  const abrirMaterial = async (resumo: MaterialResumo) => {
    setCarregandoMaterial(true); setMaterialAberto({ ...resumo, conteudo: '' });
    try { setMaterialAberto(await obterMaterial(resumo.id)); }
    catch (e) { setMaterialAberto(null); message.error(mensagemErro(e)); }
    finally { setCarregandoMaterial(false); }
  };

  const baixar = async () => {
    setBaixando(true);
    try { await baixarKit(); message.success('Download iniciado. Extraia o ZIP e abra a pasta no Codex ou Claude Code.'); }
    catch (e) { message.error(mensagemErro(e)); }
    finally { setBaixando(false); }
  };

  if (carregando) return <div className="nexo-page"><Skeleton active paragraph={{ rows: 8 }} /></div>;
  if (erro) return <div className="nexo-page"><Result status="error" title="Não foi possível carregar o NEXO" subTitle={erro}
    extra={<Button type="primary" icon={<ReloadOutlined />} onClick={carregar}>Tentar novamente</Button>} /></div>;
  if (permitido === false) return <div className="nexo-page"><Result status="403" title="Você ainda não tem acesso ao NEXO"
    subTitle="O acesso é definido em Administração → Permissões. Procure um responsável autorizado para revisar seu perfil."
    extra={<Button href="/admin/permissoes">Abrir Administração → Permissões</Button>} /></div>;
  if (!dados) return null;

  const itens = [
    { key: 'inicio', label: <span><RocketOutlined /> Comece aqui</span>, children: <PainelInicio dados={dados} materiais={materiais} abrirMaterial={abrirMaterial} trocarAba={trocarAba} /> },
    { key: 'materiais', label: <span><BookOutlined /> Materiais e downloads</span>, children: <PainelMateriais materiais={materiais} abrirMaterial={abrirMaterial} baixar={baixar} baixando={baixando} /> },
    { key: 'equipe', label: <span><TeamOutlined /> {dados.permissoes.gerenciar_colaboradores ? 'Colaboradores' : 'Minha entrada'}</span>, children: <PainelEquipe dados={dados} recarregar={carregar} /> },
    { key: 'acervo', label: <span><FolderOpenOutlined /> Acervo</span>, children: <PainelAcervo dados={dados} recarregar={carregar} /> },
  ];

  return <div className="nexo-page">
    <div className="nexo-hero">
      <div>
        <Tag color="geekblue">PILOTO INTERNO</Tag>
        <Title>NEXO</Title>
        <Paragraph>Capacidades e Evolução</Paragraph>
        <Text>Um lugar para preparar a equipe, reaproveitar o que já funciona e validar cada melhoria antes do uso.</Text>
      </div>
      <div className="nexo-hero-mark" aria-hidden="true">N</div>
    </div>
    <Tabs activeKey={aba} onChange={trocarAba} items={itens} className="nexo-tabs" />

    <Modal open={!!materialAberto} onCancel={() => setMaterialAberto(null)} footer={null}
      title={materialAberto?.titulo} width="94vw" style={{ maxWidth: 1080, top: 16 }}
      styles={{ body: { maxHeight: 'calc(100vh - 130px)', overflowY: 'auto' } }} destroyOnClose>
      {carregandoMaterial ? <Skeleton active paragraph={{ rows: 10 }} /> : materialAberto && <>
        <Text type="secondary" title={`Fonte: ${materialAberto.origem} · revisão ${materialAberto.revisao}`}>
          Fonte: <a href={fonteMaterial(materialAberto.origem).href} target="_blank" rel="noreferrer">{fonteMaterial(materialAberto.origem).rotulo}</a>
          {' · '}revisão {revisaoCurta(materialAberto.revisao)}
        </Text>
        <MarkdownSeguro conteudo={materialAberto.conteudo} origem={materialAberto.origem} materiais={materiais} abrirMaterial={abrirMaterial} />
      </>}
    </Modal>
  </div>;
}

function PainelInicio({ dados, materiais, abrirMaterial, trocarAba }: {
  dados: InicioNexo; materiais: MaterialResumo[]; abrirMaterial: (m: MaterialResumo) => void; trocarAba: (a: string) => void;
}) {
  const manual = materiais.find(material => material.id === 'manual');
  const desenvolve = dados.participante?.perfil_uso === 'desenvolve';
  const gerenciaEquipe = dados.permissoes.gerenciar_colaboradores;
  const editaAcervo = dados.permissoes.editar_acervo;
  const homologa = dados.permissoes.homologar;
  const passos = gerenciaEquipe ? [
    { numero: 1, titulo: 'Prepare sua IA', texto: 'Baixe o kit, abra a pasta no Codex ou Claude Code do computador e cole o pedido inicial.', acao: () => trocarAba('materiais'), botao: 'Preparar minha IA' },
    { numero: 2, titulo: 'Prepare a equipe', texto: 'Cadastre fichas opcionais para orientar e acompanhar as pessoas. Os acessos são definidos em Administração → Permissões.', acao: () => trocarAba('equipe'), botao: 'Ver colaboradores' },
    editaAcervo
      ? { numero: 3, titulo: 'Cadastre o que será reutilizado', texto: 'Registre uma capacidade real, cadastre sua versão e envie para o responsável autorizado homologar.', acao: () => trocarAba('acervo'), botao: 'Abrir acervo' }
      : homologa
        ? { numero: 3, titulo: 'Confira os resultados', texto: 'Revise as versões enviadas, registre o parecer e decida se podem ser usadas.', acao: () => trocarAba('acervo'), botao: 'Abrir homologações' }
      : { numero: 3, titulo: 'Consulte o que já existe', texto: 'Veja as versões homologadas e siga as instruções de uso de cada capacidade.', acao: () => trocarAba('acervo'), botao: 'Abrir acervo' },
  ] : editaAcervo ? [
    { numero: 1, titulo: 'Prepare sua IA', texto: 'Baixe o kit e abra a pasta no Codex ou Claude Code do computador. O pedido inicial está pronto para copiar.', acao: () => trocarAba('materiais'), botao: 'Preparar minha IA' },
    { numero: 2, titulo: 'Faça um primeiro teste', texto: 'O manual mostra como pedir uma melhoria pequena e conferir o resultado com a IA.', acao: manual ? () => abrirMaterial(manual) : () => trocarAba('materiais'), botao: 'Abrir manual' },
    { numero: 3, titulo: 'Registre a melhoria', texto: 'Cadastre a capacidade e a versão e, quando estiver pronta, envie para homologação.', acao: () => trocarAba('acervo'), botao: 'Abrir acervo' },
  ] : homologa ? [
    { numero: 1, titulo: 'Confira o que está aguardando', texto: 'Abra o histórico das capacidades e revise as versões enviadas para homologação.', acao: () => trocarAba('acervo'), botao: 'Abrir acervo' },
    { numero: 2, titulo: 'Registre sua decisão', texto: 'Aceite, devolva para rascunho ou suspenda uma versão com o parecer necessário.', acao: () => trocarAba('acervo'), botao: 'Ver versões' },
    { numero: 3, titulo: 'Consulte as orientações', texto: 'Use os materiais do NEXO para conferir critérios e combinar a demonstração com o responsável.', acao: manual ? () => abrirMaterial(manual) : () => trocarAba('materiais'), botao: 'Ver orientação' },
  ] : desenvolve ? [
    { numero: 1, titulo: 'Prepare sua IA', texto: 'Baixe o kit e abra a pasta no Codex ou Claude Code do computador. O pedido inicial está pronto para copiar.', acao: () => trocarAba('materiais'), botao: 'Preparar minha IA' },
    { numero: 2, titulo: 'Faça um primeiro teste', texto: 'O manual mostra como pedir uma melhoria pequena e conferir o resultado com a IA.', acao: manual ? () => abrirMaterial(manual) : () => trocarAba('materiais'), botao: 'Abrir manual' },
    { numero: 3, titulo: 'Procure o que já existe', texto: 'Consulte capacidades homologadas antes de pedir uma ferramenta nova.', acao: () => trocarAba('acervo'), botao: 'Ver capacidades' },
  ] : [
    { numero: 1, titulo: 'Encontre a ferramenta', texto: 'Abra uma capacidade homologada e leia “Como usar” para encontrar o produto ou canal de execução.', acao: () => trocarAba('acervo'), botao: 'Abrir acervo' },
    { numero: 2, titulo: 'Use ou confira o resultado', texto: 'A execução acontece na ferramenta indicada. Para conferir uma melhoria, combine uma demonstração com o responsável.', acao: manual ? () => abrirMaterial(manual) : () => trocarAba('materiais'), botao: 'Ver orientação' },
    { numero: 3, titulo: 'Informe seu avanço, se quiser', texto: 'As etapas da sua entrada são registros manuais de acompanhamento. Não liberam outros sistemas.', acao: () => trocarAba('equipe'), botao: 'Minha entrada' },
  ];

  return <div>
    <div className="nexo-welcome">
      <div><Title level={2}>{gerenciaEquipe ? 'Vamos colocar o NEXO em movimento.' : 'Bem-vindo ao NEXO.'}</Title>
        <Paragraph>{gerenciaEquipe || editaAcervo || desenvolve ? 'A IA que desenvolve é o Codex ou Claude Code aberto no seu computador. Aqui você encontra orientações, acompanhamento e acervo.' : homologa ? 'Confira as versões e registre a decisão com base nos critérios e evidências apresentados.' : 'Consulte o acervo e siga o “Como usar” da ferramenta. Você não precisa instalar uma IA para consultar o NEXO.'}</Paragraph></div>
      <CheckCircleOutlined aria-hidden="true" />
    </div>
    <div className="nexo-steps">{passos.map(passo => <Card key={passo.numero} className="nexo-step-card">
      <div className="nexo-step-number">{passo.numero}</div><Title level={4}>{passo.titulo}</Title><Paragraph>{passo.texto}</Paragraph>
      <Button type={passo.numero === 1 ? 'primary' : 'default'} onClick={passo.acao}>{passo.botao}</Button>
    </Card>)}</div>
    <Alert className="nexo-start-alert" type="info" showIcon message="O NEXO já está disponível para este piloto. Ele reúne orientações e capacidades; a execução ocorre no produto ou canal indicado em cada versão." />
  </div>;
}

function PainelMateriais({ materiais, abrirMaterial, baixar, baixando }: {
  materiais: MaterialResumo[]; abrirMaterial: (m: MaterialResumo) => void; baixar: () => void; baixando: boolean;
}) {
  const principais = materiais.filter(m => ['manual', 'contas'].includes(m.id))
    .sort((a, b) => Number(b.id === 'manual') - Number(a.id === 'manual'));
  const referencias = materiais.filter(m => !['manual', 'contas'].includes(m.id));
  const cartoes = (lista: MaterialResumo[]) => <div className="nexo-card-grid nexo-material-grid">
    {lista.map(material => <Card key={material.id} hoverable className="nexo-material-card" onClick={() => abrirMaterial(material)}>
      <FileTextOutlined className="nexo-material-icon" />
      <Title level={4}>{material.titulo}</Title>
      <Paragraph ellipsis={{ rows: 3 }}>{material.descricao}</Paragraph>
      <Button type="link">Ler material</Button>
    </Card>)}
  </div>;
  return <div>
    <Flex justify="space-between" align="flex-start" wrap="wrap" gap={12} className="nexo-section-heading">
      <div><Title level={3}>Prepare a IA no seu computador</Title><Paragraph type="secondary">Para quem vai desenvolver com Codex ou Claude Code. Quem só utiliza ferramentas pode ir direto ao Acervo.</Paragraph></div>
      <Button type="primary" icon={<CloudDownloadOutlined />} loading={baixando} onClick={baixar}>Baixar kit para a IA</Button>
    </Flex>
    <Card style={{ marginBottom: 20 }}>
      <ol>
        <li><strong>Baixe</strong> o kit pelo botão acima.</li>
        <li><strong>Extraia</strong> o ZIP para uma pasta nova, por exemplo Documentos → Hydrodata-NEXO.</li>
        <li><strong>Abra essa pasta</strong> no Codex ou no Claude Code do computador. No Claude desktop, use a aba Code e o ambiente Local.</li>
        <li><strong>Copie o pedido abaixo</strong>, cole na conversa da IA e descreva seu primeiro problema.</li>
      </ol>
      <Paragraph copyable={{ text: pedidoInicial }} style={{ whiteSpace: 'pre-line', marginBottom: 0 }}>{pedidoInicial}</Paragraph>
      <Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0 }}>A IA lê as orientações e confere a preparação. Você faz os logins e explica o resultado esperado. Se a pasta já existe, extraia o kit em outra pasta e peça à IA para conciliá-las.</Paragraph>
    </Card>
    {!materiais.length ? <Empty description="Nenhum material está disponível no momento." /> : <>
      {cartoes(principais)}
      <Collapse style={{ marginTop: 20 }} items={[{ key: 'referencias', label: 'Referências para a IA e responsáveis — consulta opcional', children: cartoes(referencias) }]} />
    </>}
  </div>;
}

export default function Nexo() {
  return <AntApp><NexoConteudo /></AntApp>;
}
